/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {SyntheticEvent, useCallback} from 'react';

/**
 * Wraps a callback that expects a domain value - not a DOM event - so it
 * can be safely used as a raw JSX event handler (e.g. `onClick`). Discards
 * whatever event it's invoked with and calls the callback with `value`
 * instead, optionally stopping the event's propagation/default action
 * first.
 *
 * ```typescript
 *
 *  import {useCallbackWithValue} from '@ion/dls/src/hooks/useCallbackWithValue';
 *
 *  const onViewHistory = useCallbackWithValue(viewHistoryClick, assetData, true);
 *  <IconButton onClick={onViewHistory} />
 *
 * ```
 *
 * @param   callback the domain callback to invoke with `value`.
 * @param   value the value to call `callback` with, in place of the event.
 * @param   stopEventPropagation when `true`, calls `stopPropagation()` and
 *          `preventDefault()` on the received event before invoking
 *          `callback`.
 * @returns a stable event handler that discards its event argument.
 */
export const useCallbackWithValue = <T>(
  callback: ((value: T) => void) | null | undefined,
  value: T,
  stopEventPropagation = false
) => {
  return useCallback((e?: SyntheticEvent) => {
    if (stopEventPropagation) {
      e?.stopPropagation();
      e?.preventDefault();
    }
    callback?.(value);
  }, [callback, value, stopEventPropagation]);
};

export default useCallbackWithValue;
